import { VideoLayer } from "@manager/layer/VideoLayer";
import { VideoLayerObserver } from "@manager/layerObserver/VideoLayerObserver";
import React from "react";
import { VintomError } from "../../api/model/error/VintomError";
import { Source } from "../../model/playerConfiguration/Source";
import { Subtitle } from "../../model/playerConfiguration/Subtitle";
import { QualityLevel } from "../../model/qualityLevel/QualityLevel";
import { SubtitlesText } from "../../model/subtitles/SubtitlesText";
import { BrowserFeatureDetector } from "../../utils/BrowserFeatureDetector";
import { ListenerHelper } from "../../utils/ListenerHelper";

import "@styles/c-videoLayer.scss";

// tslint:disable:max-classes-per-file
let initializeFadeout = true;
let initializeMusicPlay = true;
let turnOffFadeOut = false;
declare var videojs: typeof import("video.js").default;
type Player = import("video.js").default.Player;

type PlayPauseRequest = "play" | "pause" | "playPause";
type PlayPauseRequestStatus = "initial" | "inProgress" | "success" | "error";

/**
 * Calling pause while play is in progress results in error
 * Seeking while video is not playing to a location that is not buffered
 * does not reload current frame - we call play and pause to force reloading the frame.
 * There could be a better solution but I haven't found anything.
 */
class PlayPauseRequestQueue {

  private lastRequest: PlayPauseRequest;
  private lastStatus: PlayPauseRequestStatus = "initial";
  private last: Promise<void> = Promise.resolve();

  constructor(
    private play: () => Promise<void>,
    private pause: () => void,
    private onPlay: () => void,
    private onPause: () => void) { }

  get lastQueuedRequest() {
    return this.lastRequest;
  }

  push(request: PlayPauseRequest) {
    switch (this.lastStatus) {
      case "initial":
      case "error":
      case "success":
        this.doPush(request);
        break;
      case "inProgress":
        if (request === this.lastRequest && request === "playPause") {
          this.doPush(request);
        }
        if (request !== this.lastRequest && !(this.lastRequest === "playPause" && request === "pause")) {
          this.doPush(request);
        }
        break;
      default:
        throw new Error(`Unsupported PlayPauseRequestStatus '${this.lastStatus}'`);
    }
  }

  private doPush(request: PlayPauseRequest) {
    let action: () => Promise<void>;
    switch (request) {
      case "pause":
        action = () => Promise.resolve().then(() => this.pause()).then(() => this.onPause());
        break;
      case "play":
        action = () => this.play().then(() => this.onPlay());
        break;
      case "playPause":
        action = () => this.play().then(() => this.pause());
        break;
      default:
        throw new Error(`Unsupported PlayPauseRequest '${request}'`);
    }

    this.lastStatus = "inProgress";
    this.lastRequest = request;
    this.last = this.last
      .then(action)
      .then(() => { this.lastStatus = "success"; }, () => { this.lastStatus = "error"; });
  }
}

/**
 * In theory we should be able to rely on 'waiting', 'playing' events to know when the player is waiting
 * In practice that doesn't work on mobile. 'waiting' event is fired correctly but 'playing' is fired way before
 * the video starts playing. That leads to situations where loader is not displayed but the video isn't playing either.
 */
class ProgressChecker {
  isProgressing: boolean = false;

  private static INTERVAL = 100;

  private lastSeekTime: number;
  private intervalHandle: number;

  constructor(private player: videojs.default.Player) {
    this.initialize();
  }

  cleanUp() {
    if (this.intervalHandle !== undefined) {
      clearInterval(this.intervalHandle);
    }
  }

  private initialize() {
    this.intervalHandle = window.setInterval(
      () => {
        const newSeekTime = this.player.currentTime();
        if (!this.player.seeking()) {
          this.isProgressing = newSeekTime !== this.lastSeekTime;
        }
        this.lastSeekTime = newSeekTime;
      },
      ProgressChecker.INTERVAL);
  }
}

interface Props {
  isFullscreen: boolean;
  className?: string;
}
interface State {
  isVideoStarted: boolean;
}

export class VideoLayerComponent extends React.Component<Props, State> implements VideoLayer {

  private static SEEK_AFTER_LOAD_INTERVAL = 50;
  private static SEEK_AFTER_LOAD_CLOSE_ENOUGH = 0.1;
  private static DEFAULT_VTT_CUES_VERTICAL_POSITION = 100;
  private static JUST_BEFORE_END = 0.01;

  private static XMPEGURL_MIME_TYPE = "application/x-mpegurl";
  private static DASH_MIME_TYPE = "application/dash+xml";
  private static MPEG4_MIME_TYPE = "video/mp4";

  private static PREFERRED_MIME_TYPE_ORDER = [
    VideoLayerComponent.DASH_MIME_TYPE,
    VideoLayerComponent.XMPEGURL_MIME_TYPE,
    VideoLayerComponent.MPEG4_MIME_TYPE];

  private progressChecker: ProgressChecker;
  private player: Player;
  private hlsHandler: any;
  private videoNode: HTMLVideoElement;
  private textTracksBySource: { [source: string]: TextTrack } = {};
  private observer: VideoLayerObserver;
  private playPauseRequestQueue: PlayPauseRequestQueue;
  private currentFrameTime: number;
  private startTimeShift: number;
  private endTimeShift: number;

  constructor(props: Props) {
    super(props);
    this.state = {
      isVideoStarted: false
    };
  }

  render() {
    return (
      <div className={`${this.props.className || ""} c-videoLayer`}>
        <div style={{ height: "100%", width: "100%" }} data-vjs-player={true}>
          <video
            crossOrigin="anonymous"
            playsInline={true}
            webkit-playsinline="true"
            ref={(node: HTMLVideoElement) => this.videoNode = node}
            className="video-js"
          />
        </div>
      </div>
    );
  }

  componentDidMount() {
    ListenerHelper.addEventListener("window", "resize", this.requestQualityChange);
  }

  componentWillUnmount() {
    ListenerHelper.removeEventListener("window", "resize", this.requestQualityChange);
    if (this.progressChecker) {
      this.progressChecker.cleanUp;
    }
  }

  // start of VideoLayer implementation
  loadVideo(sources: Source[], subtitles: Subtitle[], progress: number): Promise<void> {
    this.currentFrameTime = 0;
    this.playPauseRequestQueue = new PlayPauseRequestQueue(
      () => this.player.play(),
      () => this.player.pause(),
      () => this.observer.onPlay(),
      () => this.observer.onPause());

    this.player.show();
    const canPlay =
      new Promise<void>((resolve) => {
        /**
         * Calling play on videojs straight after 'loadedmetadata' (without waiting for 'canplay') causes a race in videojs
         * and playback sometimes stalls on Chrome (VIN-225)
         * (This happens for DASH sources only)
         *
         * On Safari canplay is fired first after user interacts with the player
         */
        const readyEventName = (() => {
          if (
            BrowserFeatureDetector.isChrome &&
            !BrowserFeatureDetector.isMobileDevice &&
            !BrowserFeatureDetector.isAndroid
          ) {
            return "canplay";
          }
          return "loadedmetadata";
        })();

        this.player.one(readyEventName, () => {
          this.hlsHandler = (this.player.tech({ IWillNotUseThisInPlugins: true }) as any).vhs;
          this.seekAfterLoad(progress);
          resolve();
        });
      });

    this.player.src(this.prepareSources(sources));

    let loadedTracks: Promise<void>[];
    canPlay.then(() =>
      loadedTracks = subtitles.map((subtitle) => {
        const track = this.player.addRemoteTextTrack({ src: subtitle.source }, false);
        return new Promise<void>((resolve) => {
          track.addEventListener("load", () => {
            this.textTracksBySource[subtitle.source] = track.track;
            track.track.addEventListener("cuechange", () => this.handleTextTrack(track.track));
            resolve();
          });
        });
      })
    );

    const ready = canPlay.then(() => Promise.all(loadedTracks).then(() => {}));

    this.setState({
      isVideoStarted: false
    });

    return ready;
  }

  getCurrentSeekTime(): number {
    return this.player.currentTime();
  }

  getCurrentFrameTime(): number {
    if (!this.player.seeking()) {
      this.currentFrameTime = this.getCurrentSeekTime();
    }
    return this.currentFrameTime;
  }

  getTotalTime(): number {
    return this.player.duration();
  }

  getBufferedTime(): number {
    const bufferedRanges = this.getBufferedRanges();
    bufferedRanges.sort((r1, r2) => r1.start - r2.start);

    const currentTime = this.player.currentTime();
    const currentBufferedRange = bufferedRanges.find((range) => range.start <= currentTime && range.end >= currentTime);
    return currentBufferedRange && currentBufferedRange.end || 0;
  }

  getVolume(): number {
    return this.player.volume();
  }

  getIsPlaying(): boolean {
    return this.playPauseRequestQueue.lastQueuedRequest === "play" && !this.player.ended() && !this.player.seeking();
  }

  get isProgressing(): boolean {
    return this.progressChecker && this.progressChecker.isProgressing;
  }

  getIsWaiting(): boolean {
    return !this.isProgressing && !this.player.paused() && !this.player.ended();
  }

  getIsFullyLoaded(): boolean {
    return !!this.player.played().length;
  }

  play(): void {
    if (!this.player.ended()) {
      this.playPauseRequestQueue.push("play");
    }
  }

  pause(): void {
    this.playPauseRequestQueue.push("pause");
  }

  moveTo(time: number): void {
    if (time >= this.player.duration()) {
      // Don't let video end while scrubbing. It's done the same way in videojs. You cannot seek to the end.
      // It feels like playback becomes unstable in Safari after you do that.
      this.player.currentTime(time - VideoLayerComponent.JUST_BEFORE_END);
      return;
    }
    this.player.currentTime(time);
  }

  reloadFrame(): void {
    const bufferedRanges = this.getBufferedRanges();
    const currentTime = this.player.currentTime();

    const isNewFrameInRange = !!bufferedRanges.find((range) => range.start <= currentTime && range.end >= currentTime);
    if (isNewFrameInRange) {
      return;
    }

    if (this.player.ended()) {
      this.player.currentTime(this.player.currentTime() - VideoLayerComponent.JUST_BEFORE_END);
      this.playPauseRequestQueue.push("play");
    } else {
      this.playPauseRequestQueue.push("playPause");
    }
  }

  changeVolume(volume: number): void {
    this.player.volume(volume);
  }

  changeMuted(isMuted: boolean): void {
    this.player.muted(isMuted);
    if (isMuted) {
      this.player.volume(0);
    }
  }

  getBandwidth(): number {
    return this.hlsHandler && this.hlsHandler.bandwidth;
  }

  getQualityLevels(): QualityLevel[] {

    if (this.hlsHandler) {
      const currentRepresentationId = this.hlsHandler.selectPlaylist().uri;
      const representations = this.hlsHandler.representations();

      return representations
        .map((representation) => ({
          id: representation.id,
          width: representation.width,
          height: representation.height,
          bandwidth: representation.bandwidth,
          isCurrent: representation.id === currentRepresentationId
        }))
        .sort((r1, r2) => r1.bandwidth - r2.bandwidth);
    }
  }

  initializeLayer(): void {
    const playerOptions: any = {
      resizeManager: false,
      html5: {
        nativeTextTracks: false,
        nativeAudioTracks: false,
        nativeVideoTracks: false,
        vhs: {
          overrideNative: true,
          useBandwidthFromLocalStorage: true
        }
      }
    };

    this.player = videojs(this.videoNode, playerOptions);
    this.progressChecker = new ProgressChecker(this.player);
  }

  registerObserver(observer: VideoLayerObserver): void {

    if (this.player) {
      this.player.off("ended");
      this.player.off("play");
      this.player.off("error");
      this.player.off("durationchange");

      this.observer = observer;

      this.player.on("durationchange", () => observer.onTotalTimeChange(this.player.duration()));
      this.player.on("ended", () => observer.onEnded());
      this.player.on("timeupdate", () => {
        if (this.player.currentTime() >= this.startTimeShift && initializeMusicPlay) {
          if (this.getIsPlaying()) {
            observer.initializeMusic();
            initializeMusicPlay = false;
          }
        }
        if (this.player.currentTime() < this.startTimeShift) {
          initializeMusicPlay = true;
        }
        if (this.player.remainingTime() < (2 + this.endTimeShift) && initializeFadeout === true) {
          turnOffFadeOut = (this.player.remainingTime() < this.endTimeShift);
          observer.onNearTheEnd(turnOffFadeOut);
          initializeFadeout = false;
        }
      });
      this.player.on("play", () => {
        initializeFadeout = true;
        if (!this.state.isVideoStarted) {
          observer.onStarted();
          this.setState({
            isVideoStarted: true
          });
        }
      });
      this.player.on("error", () => observer.onError(new VintomError("playbackFailure", this.player.error().message)));
    }
  }
  
  setMusicTime(startTime: number, endTime: number): void {
    this.startTimeShift = startTime;
    this.endTimeShift = endTime;
  }

  captureSnapshot(): string {
    if (BrowserFeatureDetector.isSafari()) {
      // Capturing snapshots doesn't work correctly on Safari
      // in MPEG4 it's always the first frame that's captured, regardless of what's currently displayed
      // in HLS the data url is always invalid
      return;
    }

    if (this.props.isFullscreen &&
        ((BrowserFeatureDetector.isAndroid() && BrowserFeatureDetector.isChrome()) || !BrowserFeatureDetector.isFullscreenSupported())) {
      // Screenshots on Chrome on Android in fullscreen mode are always black
      // Screenshots in fullscreen imitation mode are always black 
      return;
    }

    const canvas: HTMLCanvasElement = document.createElement("canvas");
    canvas.width = this.videoNode.clientWidth;
    canvas.height = this.videoNode.clientHeight;

    try {
      canvas.getContext("2d").drawImage(this.videoNode, 0, 0, canvas.width, canvas.height);
    } catch {
      // Firefox on Android throws 'NS_ERROR_NOT_AVAILABLE: Component is not available' when drawing image from video node to canvas.
      // There are a few similar issues in Mozilla's bug tracker, ie. https://bugzilla.mozilla.org/show_bug.cgi?id=1481406
      return;
    }

    try {
      return canvas.toDataURL();
    } catch {
      // This should never happen as all video responses have Access-Control-Allow-Origin: * header
      // and the video tag has crossorigin="anonymous" attribute.
      //
      // There is a bug in Safari where it throws SecurityException even though it shouldn't.
      // Currently we do not take snapshots on Safari - they are broken both for MPEG4 and HLS - so this path
      // won't be run on Safari.
      //
      // This catch is left in case some other browser (IE maybe?) exhibits the same weird behaviour as Safari -
      // we silently fall back to not having end-of-scenario-element posters
    }
  }

  activateSubtitles(source: string) {
    Object.keys(this.textTracksBySource).forEach((trackSource) => this.textTracksBySource[trackSource].mode = "disabled");
    if (source) {
      this.textTracksBySource[source].mode = "hidden";
      this.handleTextTrack(this.textTracksBySource[source]);
    } else {
      this.observer.onSubtitlesChange(undefined);
    }
  }

  hide() {
    if (this.player) {
      this.player.hide();
    }
  }

  // end of VideoLayer implementation

  private seekAfterLoad(progress: number): void {
    if (progress === 0) {
      return;
    }
    // duration should be available after loadedmetadata event but it's not (Chrome on Android)
    const duration = this.player.duration();
    if (isNaN(duration)) {
      setTimeout(() => this.seekAfterLoad(progress), VideoLayerComponent.SEEK_AFTER_LOAD_INTERVAL);
    } else {
      const timeToSeekTo = progress * this.getTotalTime();
      this.trySeek(timeToSeekTo);
    }
  }

  /**
   *
   * Setting currentTime on mobile usually doesn't work (currentTime is still 0). It sometimes works after canplay event,
   * sometimes after canplaythrough and sometimes you have to wait even longer.
   * As a workaround we repeatedly try to set currentTime in an interval.
   *
   * Because of rounding this.player.currentTime() does not have to be exactly the same as the time we set.
   *
   * Looks ugly but videojs uses the same approach: https://github.com/brightcove/videojs-urlparams/blob/master/videojs.urlparams.js#L97-L105
   *
   */
  private trySeek(time: number): void {
    this.player.currentTime(time);
    setTimeout(
      () => {
        if (Math.abs(this.player.currentTime() - time) > VideoLayerComponent.SEEK_AFTER_LOAD_CLOSE_ENOUGH) {
          this.trySeek(time);
        }
      },
      VideoLayerComponent.SEEK_AFTER_LOAD_INTERVAL);
  }

  private getBufferedRanges() {
    const bufferedRangeObject = this.player.buffered();
    const bufferedRanges = Array.from(Array(bufferedRangeObject.length).keys())
      .map((index) => ({
        start: bufferedRangeObject.start(index),
        end: bufferedRangeObject.end(index)
      }));
    return bufferedRanges;
  }

  private prepareSources(sources: Source[]): Source[] {
    let preparedSources = BrowserFeatureDetector.isSafari() ?
      sources.filter((source) => source.type !== VideoLayerComponent.DASH_MIME_TYPE) :
      sources;
    if (!preparedSources.length) {
      this.observer.onError(new VintomError("playbackFailure", "Video has no sources (DASH sources cannot be played on Safari"));
    }

    preparedSources = preparedSources.sort((s1, s2) =>
      VideoLayerComponent.PREFERRED_MIME_TYPE_ORDER.indexOf(s1.type) - VideoLayerComponent.PREFERRED_MIME_TYPE_ORDER.indexOf(s2.type));

    return preparedSources;
  }

  private handleTextTrack(textTrack: TextTrack) {
    const activeCues = textTrack.activeCues;
    const activeVTTCues: VTTCue[] = [];

    for (const idx of Array.from(Array(activeCues.length).keys())) {
      const activeCue = activeCues[idx] as any;
      // only VTTCue has line property
      if (activeCue.line !== undefined) {
        activeVTTCues.push(activeCue as VTTCue);
      }
    }
    // there should be at most one VTTCue at a given time
    const activeVTTCue = activeVTTCues.length && activeVTTCues[0];

    let subtitlesText: SubtitlesText;

    if (activeVTTCue) {
      subtitlesText = {
        text: activeVTTCue.text,
        verticalPosition: typeof (activeVTTCue.line) === "number" ?
          activeVTTCue.line as number : VideoLayerComponent.DEFAULT_VTT_CUES_VERTICAL_POSITION
      };
    }

    this.observer.onSubtitlesChange(subtitlesText);
  }

  private requestQualityChange = () => {
    this.hlsHandler && this.hlsHandler.masterPlaylistController_.smoothQualityChange_();
  }
}

// tslint:enable:max-classes-per-file
